import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronsRight, Calendar, Facebook } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../mock";
import { fetchStories, subscribeNewsletter, errorMessage } from "../lib/api";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function Footer() {
  const [email, setEmail] = useState("");
  const [latest, setLatest] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchStories({ limit: 2 }).then((d) => setLatest(d.slice(0, 2))).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      const r = await subscribeNewsletter(email.trim());
      toast.success(r.already_subscribed ? "You are already subscribed. Thank you!" : "Thanks for subscribing to our newsletter!");
      setEmail("");
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#24231D] text-white relative grain">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand */}
        <div>
          <img src={SITE.logo} alt="EnviroJustice PR" className="h-16 w-auto object-contain mb-6 bg-white rounded-md p-1.5" />
          <p className="text-[#c9c8c0] leading-relaxed mb-8 max-w-xs">{SITE.tagline}</p>
          <Link to="/tell-your-story" className="btn-green whitespace-nowrap text-sm px-5">
            Tell Your Story <ChevronsRight size={16} />
          </Link>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="text-2xl font-extrabold mb-6">Quick Links</h4>
          <ul className="space-y-3.5 text-[#e9e8e1]">
            {[
              { label: "Meet The Team", to: "/team" },
              { label: "Join FB Community", href: SITE.facebook },
              { label: "Tell Your Story", to: "/tell-your-story" },
              { label: "Discover Stories", to: "/stories" },
              { label: "Contact Us", to: "/#contact" },
            ].map((l) => (
              <li key={l.label}>
                {l.href ? (
                  <a href={l.href} target="_blank" rel="noreferrer" className="hover:text-[#4BAF47] transition-colors inline-flex items-center gap-2 group">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4BAF47] opacity-0 group-hover:opacity-100 transition-opacity" />
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} className="hover:text-[#4BAF47] transition-colors inline-flex items-center gap-2 group">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4BAF47] opacity-0 group-hover:opacity-100 transition-opacity" />
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Educational content */}
        <div>
          <h4 className="text-2xl font-extrabold mb-6">Educational Content</h4>
          <div className="space-y-6">
            {latest.map((s) => (
              <Link key={s.id} to={`/stories/${s.slug}`} className="flex gap-4 group">
                <img src={s.image} alt="" className="h-16 w-16 rounded-md object-cover flex-shrink-0" />
                <div>
                  <p className="font-bold leading-snug group-hover:text-[#4BAF47] transition-colors line-clamp-2">{s.title}</p>
                  <p className="text-xs text-[#a8a79f] mt-1.5 flex items-center gap-1.5">
                    <Calendar size={12} /> {formatDate(s.created_at)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Newsletter */}
        <div>
          <h4 className="text-2xl font-extrabold mb-6">NewsLetter</h4>
          <p className="text-[#c9c8c0] mb-6">Sign up for our Newsletter</p>
          <form onSubmit={subscribe} className="flex">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email Address"
              data-testid="newsletter-email"
              className="flex-1 min-w-0 h-14 px-4 rounded-l-md bg-white text-[#24231D] placeholder:text-[#878680] outline-none focus:ring-2 focus:ring-[#4BAF47]"
            />
            <button type="submit" disabled={busy} data-testid="newsletter-submit" className="h-14 px-6 bg-[#4BAF47] hover:bg-[#3f9a3c] transition-colors font-bold rounded-r-md disabled:opacity-70">
              {busy ? "..." : "Submit"}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-[#4BAF47]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
          <p className="text-sm">EnviroJusticePR.org Copyright © {new Date().getFullYear()} All Rights Reserved.</p>
          <a
            href={SITE.facebook}
            target="_blank"
            rel="noreferrer"
            aria-label="Facebook"
            className="h-10 w-10 rounded-full border border-white/50 flex items-center justify-center hover:bg-white hover:text-[#4BAF47] transition-colors"
          >
            <Facebook size={16} />
          </a>
        </div>
      </div>
    </footer>
  );
}
